import React, { useState, useContext } from "react";
import PropTypes from 'prop-types';
import "../../scss/main.scss"
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconButton, Tooltip, } from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import EventRepeatTwoToneIcon from '@mui/icons-material/EventRepeatTwoTone';
import SourceTwoToneIcon from '@mui/icons-material/SourceTwoTone';
import { styled } from '@mui/material/styles';
import { I18N_CONFIG_KEY } from '../../widget/LanguageSelector';

const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator, searchTerms, showActive, showInactive) {
    if (searchTerms.length > 0) {
        let filteredArray = [];
        for (var i = 0; i < array.length; i++) {
            for (var j = 0; j < searchTerms.length; j++) {
                if (
                    array[i].company.toLowerCase().includes(searchTerms[j].toLowerCase()) ||
                    array[i].campaign.toLowerCase().includes(searchTerms[j].toLowerCase())
                ) {
                    filteredArray.push(array[i]);
                }
            }
        }
        array = JSON.parse(JSON.stringify(filteredArray));
    }
    array = array.filter((campaign) => ((showActive && !campaign.ended) || (showInactive && campaign.ended)));
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const EnhancedTableHead = (props) => {
    const { t } = useTranslation();
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    const headCells = [
        {
            id: 'campaign',
            numeric: false,
            disablePadding: true,
            label: t("campaign.campaign"),
        },
        {
            id: 'company',
            numeric: false,
            disablePadding: false,
            label: t("campaign.company"),
        },
        {
            id: 'country',
            numeric: false,
            disablePadding: false,
            label: t("campaign.country"),
        },
        {
            id: 'createdAt',
            numberic: true,
            disablePadding: false,
            label: t("campaign.createdAt")
        },
        {
            id: 'managers',
            numeric: true,
            disablePadding: false,
            label: t("campaign.managers"),
        },
        {
            id: 'candidates',
            numeric: true,
            disablePadding: false,
            label: t("campaign.candidates"),
        },
        {
            id: 'ended',
            numeric: false,
            disablePadding: true,
            label: t("campaign.status")
        },
        {
            id: 'actions',
            numeric: false,
            disablePadding: true,
            label: ''
        },
    ];

    return (
        <TableHead >
            <TableRow>

                <TableCell padding="checkbox">
                    {props.isUserAdmin && (
                        <Checkbox
                            checked={numSelected > 0}
                            onChange={onSelectAllClick}
                            disabled={numSelected == 0}
                            inputProps={{
                                'aria-label': 'select all campaigns',
                            }}
                        />
                    )}
                </TableCell>

                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'center' : 'left'}
                        style={{ fontWeight: '700', fontSize: '1.1em', textTransform: 'uppercase' }}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {headCell.id != 'actions' && (<TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>)}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    isUserAdmin: PropTypes.bool
};

const CampaignTable = (props) => {


    const navigate = useNavigate();
    const { t } = useTranslation();

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('createdAt');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = props.campaigns.map((n) => n.id);
            props.setSelected(newSelected);
            return;
        }
        props.setSelected([]);
    };

    const handleCheckboxClick = (event, id) => {
        const selectedIndex = props.selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(props.selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(props.selected.slice(1));
        } else if (selectedIndex === props.selected.length - 1) {
            newSelected = newSelected.concat(props.selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                props.selected.slice(0, selectedIndex),
                props.selected.slice(selectedIndex + 1),
            );
        }
        props.setSelected(newSelected);
    };

    const formatDateTime = (dateTimeString) => {
        const date = new Date(Date.parse(dateTimeString));
        return (date.toLocaleDateString(options) + " " + date.toLocaleTimeString());
    }

    const isSelected = (id) => props.selected.indexOf(id) !== -1;

    const visibleRows = React.useMemo(
        () =>
            stableSort(props.campaigns, getComparator(order, orderBy), props.searchTerms, props.showActive, props.showInactive),
        [order, orderBy, props.searchTerms, props.showActive, props.showInactive, props.campaigns]
    );

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2, borderRadius: '10px', position: 'relative' }}>
                    <TableContainer style={{ overflowY: 'auto', borderRadius: '10px' }} className="table-fit-screen" >
                        <Table stickyHeader aria-label="sticky table"
                            sx={{ minWidth: 750 }}
                            size={'medium'}
                        >
                            <EnhancedTableHead
                                numSelected={props.selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={props.campaigns.length}
                                isUserAdmin={props.isUserAdmin}
                            />

                            {(visibleRows.length > 0) && (
                                <TableBody>
                                    {visibleRows.map((row, index) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <StyledTableRow
                                                tabIndex={-1}
                                                key={row.id}
                                                className={(props.selected.includes(row.id) ? "bg bg-light-gray" : "")}
                                            >

                                                <TableCell padding="checkbox">
                                                    {props.isUserAdmin && (
                                                        <Checkbox
                                                            onClick={(event) => handleCheckboxClick(event, row.id)}

                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': labelId,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>

                                                <TableCell
                                                    component="th"
                                                    id={labelId}
                                                    scope="row"
                                                    padding="none"
                                                >
                                                    <a
                                                        className={"color color-medium-" + (row.ended ? "gray" : "purple")}
                                                        style={{ fontWeight: '700', fontSize: '1.2em' }}
                                                        disabled={row.ended}
                                                        onClick={() => {
                                                            if (!row.ended) {
                                                                navigate("kanban/" + row.id, { state: row });
                                                            }
                                                        }}
                                                    >
                                                        {row.campaign}
                                                    </a>
                                                    {props.isUserAdmin && (
                                                        <div style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
                                                            {row.id}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.company}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <img
                                                        loading="lazy"
                                                        width="40"
                                                        srcSet={`https://flagcdn.com/w40/${row.country.code.toLowerCase()}.png 2x`}
                                                        src={`https://flagcdn.com/w40/${row.country.code.toLowerCase()}.png`}
                                                        alt=""
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatDateTime(row.createdAt)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <span
                                                        className={"tag color color-white bg " + (row.ended ? 'bg-dark-gray' : ((row.managerCount == 0) ? "bg-medium-red" : "bg-medium-green"))}
                                                        style={{ fontSize: '18px', textAlign: 'center', fontWeight: '700', width: '50px' }}>
                                                        {row.managerCount}
                                                    </span>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <span
                                                        className={"tag color color-white bg " + (row.ended ? 'bg-dark-gray' : ((row.candidateCount == 0) ? "bg-medium-red" : "bg-medium-green"))}
                                                        style={{ fontSize: '18px', textAlign: 'center', fontWeight: '700', width: '50px' }}>
                                                        {row.candidateCount}
                                                    </span>
                                                </TableCell>

                                                <TableCell>
                                                    <Tooltip title={t(row.ended ? 'campaign.ended' : 'campaign.inProgress')}>
                                                        {row.ended ? (
                                                            <EventAvailableTwoToneIcon fontSize="large" className="color color-medium-gray" />
                                                        ) :
                                                            (
                                                                <EventRepeatTwoToneIcon fontSize="large" className="color color-medium-gray" />
                                                            )}
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {(props.isUserAdmin) && (
                                                        <Tooltip title={t('campaign.edit')}>
                                                            <IconButton
                                                                disabled={row.ended}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    props.openCampaignDialog(row.id);
                                                                }}
                                                            >
                                                                <EditTwoToneIcon fontSize="large" className={"color color-medium-" + (row.ended ? 'gray' : 'blue')} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                    {(visibleRows.length == 0) && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', width: '100%', position: 'absolute', top: '140px' }}>
                            <Box style={{ display: 'flex', flexDirection: 'row' }}>
                                <div style={{ marginRight: '10px', fontSize: '1.2em', fontStyle: 'bold' }}>{t('campaign.noData')}</div>
                                <SourceTwoToneIcon />
                            </Box>
                        </div>
                    )}
                </Paper>
            </Box>
        </>
    );
};

CampaignTable.propTypes = {
    campaigns: PropTypes.array,
    selected: PropTypes.array,
    setSelected: PropTypes.func,
    openCampaignDialog: PropTypes.func,
    openCampaignDialog: PropTypes.func,
    searchTerms: PropTypes.array,
    showActive: PropTypes.bool,
    showInactive: PropTypes.bool,
    isUserAdmin: PropTypes.bool
};

export default CampaignTable;